import { useCastersContext } from "@/pages/caster/caster-data-entry-v2/CastersContext";
import { useSaveStrand } from "@/pages/caster/caster-data-entry-v2/hooks/useSaveStrand";
import { StrandData } from "@/pages/caster/models/StrandData";
import { useModalStyles } from "@/pages/caster/styles/modalStyles";
import {
	ButtonWithLoading,
	ErrorModal,
	HelmetDexteel,
	MesfModal,
} from "@dexteel/mesf-core";
import {
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { TextFieldWithCheckbox } from "../../../form-fields/common/CheckboxFieldDE";

interface EditStrandModalProps {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	strandId: string;
}

type FormValues = {
	HousingId: number | "";
	ForceHeatsOnHousing: boolean;
	MoldId: number | "";
	ForceHeatsOnMold: boolean;
};

export const EditStrandModal: React.FC<EditStrandModalProps> = ({
	show,
	onHide,
	strandId,
}) => {
	const { state } = useCastersContext();
	const classes = useModalStyles();
	const { save: saveStrand } = useSaveStrand();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");

	const housings = state.housings || [];
	const moldSizes = state.moldSizes || [];
	const strands: StrandData[] = state.strands || [];
	const cleanStrandId = strandId.startsWith("s")
		? strandId.substring(1)
		: strandId;
	const strand = strands.find(
		(s: StrandData) => s.Strand?.toString() === cleanStrandId,
	);
	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<FormValues>({
		defaultValues: {
			HousingId: strand ? strand.HousingId || "" : "",
			ForceHeatsOnHousing: strand ? !!strand.ForceHeatsOnHousing : false,
			MoldId: strand ? strand.MoldId || "" : "",
			ForceHeatsOnMold: strand ? !!strand.ForceHeatsOnMold : false,
		},
	});

	useEffect(() => {
		if (show && strand) {
			reset({
				HousingId: strand.HousingId || "",
				ForceHeatsOnHousing: !!strand.ForceHeatsOnHousing,
				MoldId: strand.MoldId || "",
				ForceHeatsOnMold: !!strand.ForceHeatsOnMold,
			});
		}
	}, [show, reset, strand]);

	const onSubmit: SubmitHandler<FormValues> = async (data) => {
		if (!strands.length) return;
		setIsSubmitLoading(true);
		try {
			await saveStrand(strand?.Strand || 0, "HousingId", data.HousingId);
			await saveStrand(
				strand?.Strand || 0,
				"ForceHeatsOnHousing",
				data.ForceHeatsOnHousing ? 1 : null,
			);
			await saveStrand(strand?.Strand || 0, "MoldId", data.MoldId);
			await saveStrand(
				strand?.Strand || 0,
				"ForceHeatsOnMold",
				data.ForceHeatsOnMold ? 1 : null,
			);

			setIsSubmitLoading(false);
			onHide(true);
		} catch (err: any) {
			setError(err.message || "Error saving strand");
			setIsSubmitLoading(false);
		}
	};

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`Edit Strand ${strand?.Strand ? strand?.Strand : ""}`}
				/>
			)}
			<MesfModal
				title={`Edit Strand ${strand?.Strand ? strand?.Strand : ""}`}
				open={show}
				handleClose={() => onHide(false)}
				id="edit-strand-modal"
				maxWidth="sm"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers style={{ padding: "15px 30px" }}>
						<Grid container spacing={2}>
							<Grid size={{ xs: 12, md: 6 }}>
								<FormControl variant="outlined" fullWidth size="small">
									<InputLabel id="housing-label">Housing</InputLabel>
									<Controller
										name="HousingId"
										control={control}
										rules={{ required: "Housing is required" }}
										render={({ field }) => (
											<Select
												{...field}
												labelId="housing-label"
												label="Housing"
											>
												{housings.map((option: any) => (
													<MenuItem
														key={option.HousingId}
														value={option.HousingId}
													>
														{option.HousingName}
													</MenuItem>
												))}
											</Select>
										)}
									/>
									{errors.HousingId && (
										<span className={classes.errorLabel}>
											{errors.HousingId.message}
										</span>
									)}
								</FormControl>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<Controller
									name="ForceHeatsOnHousing"
									control={control}
									render={({ field }) => (
										<TextFieldWithCheckbox
											label="Housing First"
											checkboxValue={!!field.value}
											disabled={false}
											onCheckboxChange={(checked) => field.onChange(checked)}
										/>
									)}
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<FormControl variant="outlined" fullWidth size="small">
									<InputLabel id="mold-label">Mold</InputLabel>
									<Controller
										name="MoldId"
										control={control}
										rules={{ required: "Mold is required" }}
										render={({ field }) => (
											<Select {...field} labelId="mold-label" label="Mold">
												{moldSizes.map((option: any) => (
													<MenuItem key={option.MoldId} value={option.MoldId}>
														{`${option.MoldName} - ${option.MoldSizeName}`}
													</MenuItem>
												))}
											</Select>
										)}
									/>
									{errors.MoldId && (
										<span className={classes.errorLabel}>
											{errors.MoldId.message}
										</span>
									)}
								</FormControl>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<Controller
									name="ForceHeatsOnMold"
									control={control}
									render={({ field }) => (
										<TextFieldWithCheckbox
											label="Mold First"
											checkboxValue={!!field.value}
											disabled={false}
											onCheckboxChange={(checked) => field.onChange(checked)}
										/>
									)}
								/>
							</Grid>
						</Grid>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px" }}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid size={{ xs: 12, md: 6 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
								>
									Cancel
								</Button>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<ButtonWithLoading
									fullWidth
									isLoading={isSubmitLoading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Save
								</ButtonWithLoading>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				</form>
			</MesfModal>

			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Editing Strand"
			/>
		</>
	);
};
